import {
    ChangeDetectorRef,
    Component,
    ComponentRef,
    Inject,
    InjectionToken,
    Input,
    Optional,
    Type,
    ViewEncapsulation,
} from '@angular/core';
import { DataFieldModel } from '../data-field.model';
import { DataFieldPropertyType } from '../data-field-property-type';
import { DataFieldType } from '../data-field-type';
import { DataFieldEdit_DataField, DataFieldEdit_DataFieldObject, DataFieldEditBase } from './data-field-edit-base';
import { DataFieldEditNameComponent } from './name/name.component';
import { DataFieldEditDescriptionComponent } from './description/description.component';
import { DataFieldEditClusterComponent } from './cluster/cluster.component';
import { DataFieldEditAreasComponent } from './areas/areas.component';
import { DataFieldEditBusinessAreasComponent } from './business-areas/business-areas.component';
import { DataFieldEditPhoneNumberComponent } from './phone-number/phone-number.component';
import { DataFieldEditEmailAddressComponent } from './email-address/email-address.component';
import { DataFieldEditAddressComponent } from './address/address.component';
import { DataFieldEditCustomTextComponent } from './custom-text/custom-text.component';
import { DataFieldEditCustomNumericComponent } from './custom-numeric/custom-numeric.component';
import { DataFieldEditCustomCalendarComponent } from './custom-calendar/custom-calendar.component';
import { DataFieldEditCustomPreSetOptionComponent } from './custom-pre-set-options/custom-pre-set-options.component';
import { DataFieldEditPasswordComponent } from './password/password.component';
import { DataFieldEditActiveComponent } from './active/active.component';
import { DataFieldEditRolesComponent } from './roles/roles.component';
import { DataFieldEditUnitsComponent } from './units/units.component';
import { DataFieldEditLanguagesComponent } from './languages/languages.component';
import { DataFieldEditTagsComponent } from './tags/tags.component';
import { FiDataFieldEditOrgNrComponent } from '@fi-app/data-field/data-field-edit/org-nr/org-nr.component';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';

type TDataFieldEdit_ComponentTypeFactory = (dataFieldModel: DataFieldModel) => Type<DataFieldEditBase>;
export const DataFieldEdit_ComponentTypeFactory = new InjectionToken<TDataFieldEdit_ComponentTypeFactory>(
    'DataFieldEdit_ComponentTypeFactory',
);

@Component({
    selector: 'data-field-edit',
    template: `<div
        [fiComponentOutlet]="getComponentType()"
        (componentCreate)="onDataFieldComponentCreated($event)"
    ></div>`,
    providers: [
        {
            provide: DataFieldEdit_DataField,
            useFactory: (dataFieldEditComponent: DataFieldEditComponent) => dataFieldEditComponent.dataField,
            deps: [DataFieldEditComponent],
        },
        {
            provide: DataFieldEdit_DataFieldObject,
            useFactory: (dataFieldEditComponent: DataFieldEditComponent) => dataFieldEditComponent.dataFieldObject,
            deps: [DataFieldEditComponent],
        },
        {
            provide: FiComponentsContainer,
            useExisting: DataFieldEditComponent,
        },
    ],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'data-field-edit' },
    standalone: false,
})
export class DataFieldEditComponent extends FiComponentsContainer {
    @Input() dataField: DataFieldModel;
    @Input() dataFieldObject: any;

    constructor(
        private cdr: ChangeDetectorRef,
        // good entry point to override "getComponentType()" method behaviour
        @Optional()
        @Inject(DataFieldEdit_ComponentTypeFactory)
        private componentTypeFactory: TDataFieldEdit_ComponentTypeFactory,
    ) {
        super();
    }

    public getComponentType() {
        if (this.componentTypeFactory) {
            const componentType = this.componentTypeFactory(this.dataField);
            if (componentType) return componentType;
        }

        switch (this.dataField.PropertyType) {
            case DataFieldPropertyType.Name:
                return DataFieldEditNameComponent;
            case DataFieldPropertyType.Description:
                return DataFieldEditDescriptionComponent;
            case DataFieldPropertyType.Cluster:
                return DataFieldEditClusterComponent;
            case DataFieldPropertyType.Areas:
                return DataFieldEditAreasComponent;
            case DataFieldPropertyType.BusinessAreas:
                return DataFieldEditBusinessAreasComponent;
            case DataFieldPropertyType.PhoneNumber:
                return DataFieldEditPhoneNumberComponent;
            case DataFieldPropertyType.EmailAddress:
                return DataFieldEditEmailAddressComponent;
            case DataFieldPropertyType.Address:
                return DataFieldEditAddressComponent;
            case DataFieldPropertyType.Password:
                return DataFieldEditPasswordComponent;
            case DataFieldPropertyType.Active:
                return DataFieldEditActiveComponent;
            case DataFieldPropertyType.Roles:
                return DataFieldEditRolesComponent;
            case DataFieldPropertyType.Tags:
                return DataFieldEditTagsComponent;
            case DataFieldPropertyType.Units:
                return DataFieldEditUnitsComponent;
            case DataFieldPropertyType.Languages:
                return DataFieldEditLanguagesComponent;
            case DataFieldPropertyType.PKOrgNr:
                return FiDataFieldEditOrgNrComponent;
            case DataFieldPropertyType.Custom: {
                switch (this.dataField.Type) {
                    case DataFieldType.Text:
                        return DataFieldEditCustomTextComponent;
                    case DataFieldType.Numeric:
                        return DataFieldEditCustomNumericComponent;
                    case DataFieldType.Calendar:
                        return DataFieldEditCustomCalendarComponent;
                    case DataFieldType.PreSetOptions:
                        return DataFieldEditCustomPreSetOptionComponent;
                    default:
                        throw `FI: DataFieldEditComponent::getComponentType() unknown Type ${this.dataField.Type}`;
                }
            }
            default:
                throw `FI: DataFieldEditComponent::getComponentType() unknown PropertyType ${this.dataField.PropertyType}`;
        }
    }

    private childComponents: FiComponentsContainer[] = [];
    onDataFieldComponentCreated(componentRef: ComponentRef<DataFieldEditBase>) {
        this.cdr.detectChanges();
        this.childComponents.push(componentRef.instance);
    }

    protected getChildContainers(): FiComponentsContainer[] {
        return this.childComponents;
    }
}
